import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useUser } from './use-user';
import { toast } from 'sonner';
import { 
  Appointment, 
  CreateAppointmentDto, 
  UpdateAppointmentDto,
  AppointmentStatus,
  AppointmentType
} from '../interfaces/appointment.interface';

interface UseAppointmentsOptions {
  from?: Date;
  to?: Date;
  dealId?: string;
  assignedTo?: string;
  status?: AppointmentStatus;
}

export const useAppointments = (options: UseAppointmentsOptions = {}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useUser();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    // TEMPORARY: Check localStorage first, then use dummy data
    if (options.dealId) {
      // For deal-specific view, check deal appointments
      const storedAppointments = localStorage.getItem(`appointments_${options.dealId}`);
      if (storedAppointments) {
        const parsed = JSON.parse(storedAppointments);
        setAppointments(parsed);
        setLoading(false);
        return;
      }
    } else {
      // For general calendar view, get all appointments
      const allAppointments = localStorage.getItem('appointments_all');
      if (allAppointments) {
        const parsed = JSON.parse(allAppointments);
        setAppointments(parsed);
        setLoading(false);
        return;
      }
    }
    
    // Use dummy data if no stored appointments
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const dummyAppointments: Appointment[] = [
      {
        _id: '1',
        title: 'Anruf mit Maurice Lange',
        type: AppointmentType.CALL,
        start: new Date(today.setHours(10, 0, 0, 0)).toISOString(),
        end: new Date(today.setHours(10, 30, 0, 0)).toISOString(),
        location: '',
        description: 'Erstgespräch Blockalarm System',
        status: AppointmentStatus.SCHEDULED,
        participantUserIds: ['7'], // Freddy Blum
        dealId: options.dealId || '123',
        contactId: '456',
        organizerUserId: '17', // Andi Hysa
        timezone: 'Europe/Berlin',
        allDay: false,
        reminders: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        _id: '2',
        title: 'Meeting mit Andreas Osterrieder',
        type: AppointmentType.MEETING,
        start: new Date(today.setHours(14, 0, 0, 0)).toISOString(),
        end: new Date(today.setHours(15, 0, 0, 0)).toISOString(),
        location: 'Büro München',
        description: 'Vertragsverhandlung',
        status: AppointmentStatus.SCHEDULED,
        participantUserIds: ['1', '7'], // Andreas & Freddy
        dealId: options.dealId || '124',
        contactId: '457',
        organizerUserId: '5', // Dirk Bienert
        timezone: 'Europe/Berlin',
        allDay: false,
        reminders: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        _id: '3',
        title: 'Follow-up Call',
        type: AppointmentType.CALL,
        start: new Date(tomorrow.setHours(11, 0, 0, 0)).toISOString(),
        end: new Date(tomorrow.setHours(11, 30, 0, 0)).toISOString(),
        location: '',
        description: 'Nachfassen zum Angebot',
        status: AppointmentStatus.SCHEDULED,
        participantUserIds: ['6'], // Felix Endres
        dealId: options.dealId || '125',
        contactId: '458',
        organizerUserId: '6',
        timezone: 'Europe/Berlin',
        allDay: false,
        reminders: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    ];
    
    // Filter by options if provided
    let filteredAppointments = dummyAppointments;
    
    if (options.dealId) {
      filteredAppointments = filteredAppointments.filter(a => a.dealId === options.dealId);
    }
    
    if (options.status) {
      filteredAppointments = filteredAppointments.filter(a => a.status === options.status);
    }
    
    setAppointments(filteredAppointments);
    setLoading(false);
    
    // Store dummy appointments in localStorage
    if (options.dealId && filteredAppointments.length > 0) {
      localStorage.setItem(`appointments_${options.dealId}`, JSON.stringify(filteredAppointments));
    }
    // Also store all dummy appointments globally
    if (dummyAppointments.length > 0) {
      localStorage.setItem('appointments_all', JSON.stringify(dummyAppointments));
    }
    
    /* UNCOMMENT WHEN API IS FIXED
    try {
      const token = await getAccessTokenSilently();
      const queryParams = new URLSearchParams();
      
      if (options.from) queryParams.append('from', options.from.toISOString());
      if (options.to) queryParams.append('to', options.to.toISOString());
      if (options.dealId) queryParams.append('dealId', options.dealId);
      if (options.assignedTo) queryParams.append('assignedTo', options.assignedTo);
      if (options.status) queryParams.append('status', options.status);

      const response = await fetch(
        `${process.env.REACT_APP_API}/v1.0/calendar/events?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          // Calendar endpoint might not exist, return empty array
          setAppointments([]);
          return;
        }
        throw new Error('Failed to fetch appointments');
      }

      const data = await response.json();
      setAppointments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setAppointments([]);
      // Only show error toast if it's not an auth error
      if (err instanceof Error && !err.message.includes('401')) {
        toast.error('Fehler beim Laden der Termine');
      }
    } finally {
      setLoading(false);
    }
    */
  }, [getAccessTokenSilently, options.from, options.to, options.dealId, options.assignedTo, options.status]);

  const createAppointment = async (dto: CreateAppointmentDto): Promise<Appointment | null> => {
    if (!user) {
      toast.error('Benutzer nicht gefunden');
      return null;
    }
    
    // TEMPORARY: Create dummy appointment until API is fixed
    const newAppointment: Appointment = {
      _id: Date.now().toString(),
      title: dto.title,
      type: dto.type,
      start: dto.start,
      end: dto.end,
      location: dto.location || '',
      description: dto.description || '',
      status: AppointmentStatus.SCHEDULED,
      participantUserIds: dto.participantUserIds || [],
      dealId: dto.dealId,
      contactId: dto.contactId || '',
      organizerUserId: user?._id || 'unknown', // Current user
      timezone: dto.timezone,
      allDay: dto.allDay,
      reminders: dto.reminders || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    // Add to current appointments
    setAppointments(prev => {
      const updated = [...prev, newAppointment];
      // Store in localStorage temporarily until API is fixed
      if (dto.dealId) {
        localStorage.setItem(`appointments_${dto.dealId}`, JSON.stringify(updated));
      }
      // Also store in global appointments list
      const allAppointments = JSON.parse(localStorage.getItem('appointments_all') || '[]');
      const updatedAll = [...allAppointments, newAppointment];
      localStorage.setItem('appointments_all', JSON.stringify(updatedAll));
      return updated;
    });
    toast.success('Termin erfolgreich erstellt');
    
    // Dispatch custom event for real-time updates
    window.dispatchEvent(new CustomEvent('appointmentCreated', { detail: { dealId: dto.dealId } }));
    
    // Also add to history tracking
    const historyEntry = {
      _id: `local_${Date.now()}`,
      dealId: dto.dealId,
      belongsToUserId: user?._id || 'unknown', // Current user
      historyType: 'ACTIVITY',
      historyNoteContent: {
        _id: `note_${Date.now()}`,
        description: `Termin erstellt: ${dto.title} - ${dto.type} um ${new Date(dto.start).toLocaleString('de-DE')}`
      },
      createdAt: new Date().toISOString(),
      belongsToUserFullName: user ? `${user.firstName} ${user.lastName}` : 'Unbekannter Benutzer', // Current user
    };
    const existingHistory = JSON.parse(localStorage.getItem(`deal_history_${dto.dealId}`) || '[]');
    localStorage.setItem(`deal_history_${dto.dealId}`, JSON.stringify([historyEntry, ...existingHistory]));
    
    return newAppointment;
    
    /* UNCOMMENT WHEN API IS FIXED
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API}/v1.0/calendar/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dto),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create appointment');
      }

      const newAppointment = await response.json();
      toast.success('Termin erfolgreich erstellt');
      
      // Refresh appointments
      await fetchAppointments();
      
      return newAppointment;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Erstellen des Termins';
      toast.error(message);
      return null;
    }
    */
  };

  const updateAppointment = async (id: string, dto: UpdateAppointmentDto): Promise<Appointment | null> => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API}/v1.0/calendar/events/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dto),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update appointment');
      }

      const updatedAppointment = await response.json();
      toast.success('Termin erfolgreich aktualisiert');
      
      // Refresh appointments
      await fetchAppointments();
      
      return updatedAppointment;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Aktualisieren des Termins';
      toast.error(message);
      return null;
    }
  };

  const markAsComplete = async (id: string, notes?: string): Promise<Appointment | null> => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API}/v1.0/calendar/events/${id}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ notes }),
      });

      if (!response.ok) throw new Error('Failed to mark appointment as complete');

      const updatedAppointment = await response.json();
      toast.success('Termin als erledigt markiert');
      
      // Refresh appointments
      await fetchAppointments();
      
      return updatedAppointment;
    } catch (err) {
      toast.error('Fehler beim Abschließen des Termins');
      return null;
    }
  };

  const deleteAppointment = async (id: string): Promise<boolean> => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API}/v1.0/calendar/events/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to delete appointment');

      toast.success('Termin erfolgreich abgesagt');
      
      // Refresh appointments
      await fetchAppointments();
      
      return true;
    } catch (err) {
      toast.error('Fehler beim Absagen des Termins');
      return false;
    }
  };

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.from?.toISOString(), options.to?.toISOString(), options.dealId, options.assignedTo, options.status]);

  const getAppointmentsByDeal = async (dealId: string): Promise<Appointment[]> => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API}/v1.0/calendar/deals/${dealId}/events`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch appointments for deal');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching appointments by deal:', error);
      throw error;
    }
  };

  return {
    appointments,
    loading,
    error,
    createAppointment,
    updateAppointment,
    markAsComplete,
    deleteAppointment,
    refetch: fetchAppointments,
    getAppointmentsByDeal,
  };
};
